import { ButtonCustom } from "../../../../components/custom/button-custom";
import ImageCustom from "../../../../components/custom/image-custom";
import { ModalCustom } from "../../../../components/custom/modal-custom";
import { ABSENCE_TYPE, ATTENDANCE_STATUS } from "../../../../constants/status";
import { AttendanceRecord } from "../types/attendance";
import { formatCurrencyDecimal } from "../../../../utils/helpers";
import { convertDateFormat } from "../../../../utils/time-utils";

const AttendanceManagementDetailModal = ({
    isShow,
    onClose,
    selectedRecord
}: {
    isShow: boolean;
    onClose: () => void;
    selectedRecord: AttendanceRecord[];
}) => {
    const records = selectedRecord ?? [];
    const dateLabel = records[0]?.date ?? "-";
    const totalLate = records.reduce((s, r) => s + (r?.minutes_late || 0), 0);
    const totalEarly = records.reduce((s, r) => s + (r?.minutes_early || 0), 0);
    const hasMultiple = records.length > 1;

    const formatTime = (datetime?: string): string => {
        if (!datetime) return "-";
        return convertDateFormat(datetime, "dd/MM/yyyy HH:mm", "HH:mm");
    };

    const footerModal = (
        <ButtonCustom
            type="button"
            text="Đóng"
            onClick={onClose}
            className="text-gray-500 uppercase bg-gray-100 btn hover:bg-gray-400 hover:text-white"
        />
    );

    const bodyModal = (
        <div className="space-y-6 text-sm">
            {/* --- Tổng quan ngày --- */}
            <div className="bg-white border rounded-lg">
                <div className="grid grid-cols-3 gap-4 p-4">
                    <div>
                        <div className="text-gray-500">Ngày</div>
                        <div className="font-medium">{dateLabel}</div>
                    </div>
                    <div>
                        <div className="text-gray-500">Số lần chấm công</div>
                        <div className="font-medium">
                            {records.length} lần
                        </div>
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                Đi muộn: {formatCurrencyDecimal(totalLate)} phút
                            </span>
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                Về sớm: {formatCurrencyDecimal(totalEarly)} phút
                            </span>
                        </div>
                        {hasMultiple && (
                            <div className="mt-2">
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                    Nhiều lần chấm công trong ngày
                                </span>
                            </div>
                        )}
                    </div>
                    {records.some(rec => rec.shift && rec.absence_type !== ABSENCE_TYPE.NO_SHIFT) && (
                        <div>
                            <div className="mb-1 text-gray-500">Ca làm việc</div>
                            <div className="flex items-stretch p-2 text-blue-600 border border-blue-200 rounded-md bg-blue-50 w-fit">
                                <span className="flex-shrink-0 w-1 bg-blue-500 rounded-full" />
                                <div className="flex flex-col flex-1 px-2 py-1 break-words whitespace-normal">
                                    <span className="text-base font-bold">
                                        {records[records.length - 1].shift.start_time} - {records[records.length - 1].shift.end_time}
                                    </span>
                                    <span className="text-xs">{records[records.length - 1].shift.name}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* --- Danh sách từng lần chấm công --- */}
            {records.length === 0 ? (
                <div className="p-4 text-center text-gray-500 rounded-lg bg-gray-50">
                    Không có dữ liệu chấm công.
                </div>
            ) : (
                <ul className="space-y-4">
                    {records.map((rec, idx) => {
                        const notCheckedIn =
                            rec.check_in_status === ATTENDANCE_STATUS.NOT_CHECKED_IN;
                        const notCheckedOut =
                            rec.check_out_status === ATTENDANCE_STATUS.NOT_CHECKED_OUT;

                        return (
                            <li
                                key={`${rec.id || idx}`}
                                className="p-4 bg-white border rounded-lg"
                            >
                                {/* Header mỗi lần */}
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">
                                            Lần {idx + 1}
                                        </span>
                                        <span className="text-sm font-semibold text-gray-800">
                                            {formatTime(rec.check_in_at)} —{" "}
                                            {rec.check_out_at ? formatTime(rec.check_out_at) : "…"}
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        {rec.minutes_late > 0 && (
                                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                                Đi muộn {formatCurrencyDecimal(rec.minutes_late)} phút
                                            </span>
                                        )}
                                        {rec.minutes_early > 0 && (
                                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                                Về sớm {formatCurrencyDecimal(rec.minutes_early)} phút
                                            </span>
                                        )}
                                        {notCheckedOut && (
                                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                                Chưa check out
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Nội dung mỗi lần */}
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Giờ vào:</span>
                                            <span>{notCheckedIn ? "-" : formatTime(rec.check_in_at)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Giờ ra:</span>
                                            <span>
                                                {rec.check_out_at ? formatTime(rec.check_out_at) : "Chưa check out"}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Đi muộn:</span>
                                            <span className="font-medium text-orange-600">
                                                {formatCurrencyDecimal(rec.minutes_late)} phút
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Về sớm:</span>
                                            <span className="font-medium text-purple-600">
                                                {formatCurrencyDecimal(rec.minutes_early)} phút
                                            </span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <span className="block mb-1 text-gray-500">Ảnh check-in</span>
                                            {notCheckedIn ? (
                                                <span className="text-gray-400">-</span>
                                            ) : (
                                                <div className="w-16 h-16 overflow-hidden border rounded-md">
                                                    <ImageCustom
                                                        isUser={false}
                                                        isReview
                                                        url={rec.avatar_check_in}
                                                        isLocal={false}
                                                        className="w-full h-full"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <span className="block mb-1 text-gray-500">Ảnh check-out</span>
                                            {notCheckedOut ? (
                                                <span className="text-gray-400">-</span>
                                            ) : (
                                                <div className="w-16 h-16 overflow-hidden border rounded-md">
                                                    <ImageCustom
                                                        isUser={false}
                                                        isReview
                                                        url={rec.avatar_check_out}
                                                        isLocal={false}
                                                        className="w-full h-full"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Địa chỉ & Ca làm việc & Ghi chú */}
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    <div className="space-y-3">
                                        <div>
                                            <span className="block mb-1 text-gray-500">Địa chỉ check-in</span>
                                            <div className="p-2 text-xs text-gray-600 rounded bg-gray-50">
                                                {rec.address_check_in || "Không có thông tin"}
                                            </div>
                                        </div>
                                        {rec.address_check_out && (
                                            <div>
                                                <span className="block mb-1 text-gray-500">Địa chỉ check-out</span>
                                                <div className="p-2 text-xs text-gray-600 rounded bg-gray-50">
                                                    {rec.address_check_out}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        {rec.note && (
                                            <div>
                                                <span className="block mb-1 text-gray-500">Ghi chú duyệt phiếu</span>
                                                <span className="text-sm text-gray-700">{rec.note}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );

    return (
        <ModalCustom
            isOpen={isShow}
            onClose={onClose}
            titleModal="Chi tiết chấm công"
            footerModal={footerModal}
            bodyModal={bodyModal}
        />
    );
};

export default AttendanceManagementDetailModal;
